import axiosNest from '../../api/axiosNest';
import type { TmsAlert } from '../userOffers/orderActionsApi';
import type { FleetShow } from './fleetScheduleRules';
import type { WindowBracket } from './bracketRules';

/**
 * Plan pracy auta i harmonogram floty — wywołania API.
 *
 * Odpowiada `apps/api/src/vehicle-schedules/vehicle-plan.ts`. Plan bierze
 * się ze zleceń (obsadzenia) i kandydatur (warianty), nie z okien
 * miesięcznych — te są tylko kontekstem (serwis). Pasek obsadzenia to
 * szacowana zajętość auta (`occupancyFrom`–`occupancyTo`), nie całe okno
 * zlecenia; `occupancyBasis: 'window'` znaczy, że trasy nie było i pasek
 * zajmuje całe okno.
 */

export interface PlanItem {
  kind: 'assignment' | 'candidate';
  orderId: number;
  number: string | null;
  fromCity: string | null;
  fromCountry: string | null;
  toCity: string | null;
  toCountry: string | null;
  windowFrom: string | null;
  windowTo: string | null;
  loadingFrom: string | null;
  loadingTo: string | null;
  unloadingFrom: string | null;
  unloadingTo: string | null;
  occupancyFrom: string | null;
  occupancyTo: string | null;
  occupancyBasis: 'estimate' | 'window';
  /**
   * „Zajęte do” do wyświetlenia (12.10.2026, decyzja użytkownika): późniejsza
   * z `occupancyTo` (szacunek) i końca z planu TMS przy punktach. Tylko
   * informacja — reguły liczą szacunkiem. Brak — starsze API (= `occupancyTo`).
   */
  occupiedUntil?: string | null;
  /** Skąd `occupiedUntil`; brak — szacunek. */
  occupiedUntilSource?: 'tmsPlan' | 'estimate';
  /**
   * Odcinek „wg planu TMS” za paskiem szacunku: od `occupancyTo` do końca
   * z planu TMS, gdy plan kończy później. Brak — plan nie kończy później,
   * nie ma planu albo starsze API.
   */
  tmsPlanSegment?: TmsPlanSegment;
  /**
   * Szacunek wykonania rozpisany na odcinki (06.10.2026, API
   * `vehicle-schedules/plan-segments.ts`): dolot przed `occupancyFrom`, potem
   * odcinki kończące się na `occupancyTo`. Pusta lista albo brak pola
   * (starsze API) — pasek jednym blokiem, jak dotąd.
   */
  segments?: PlanSegment[];
  /** Uwagi do odcinków: czemu nie ma dolotu, pasek ucięty na końcu okna… */
  segmentNotes?: string[];
  /**
   * Szacowany koniec wykonania po końcu okna rozładunku (07.10.2026) —
   * odcinek spóźnienia na osi. Brak — bez spóźnienia albo starsze API.
   */
  overrunUntil?: string;
  /**
   * Dwie liczby spóźnienia z opisem źródła przy pozycji ze spóźnieniem
   * (22.10.2026, decyzja użytkownika pkt 4) — kształt `LatenessFigures`
   * (`userOffers/variantWindow.ts`), czytany przez `latenessOf`. Brak —
   * przełącznik wyłączony, bez spóźnienia albo starsze API.
   */
  lateness?: unknown;
  /**
   * Terminy pierwszego załadunku i ostatniego rozładunku z planu TMS
   * (`router_…`) — okna Z/R na karcie pojazdu, gdy różnią się od okna
   * klienta. Brak — starsze API albo zlecenie bez punktów.
   */
  pointWindows?: PlanPointWindows | null;
  /**
   * Okna klienta przy punktach jako nawiasy Z1, R1, R2… (24.10.2026,
   * `bracketRules.ts`). Brak — przełącznik `ganttWindowBrackets`
   * wyłączony albo starsze API.
   */
  windowBrackets?: WindowBracket[];
  /**
   * Alerty obsadzenia czekające na potwierdzenie na karcie zlecenia
   * (przeplanowanie, zmiana w TMS) — tylko plan jednego auta.
   */
  alerts?: TmsAlert[];
  tmsOrderStatus: string | null;
  tmsOrderStatusLabel: string | null;
  planningStatus: string;
  /** `UserOffer.sourceSystem`; brak pola — starsze API. */
  orderSourceSystem?: string | null;
  /** Kolumna „Źródło” (tylko plan jednego auta); brak — starsze API, pokazujemy „—”. */
  source?: PlanSource;
  assignedElsewhere?: boolean;
  candidateId?: number;
  candidateOrigin?: string | null;
  candidateSince?: string | null;
  assignedBy?: { username: string | null; at: string; source: string | null } | null;
}

export interface PlanPointWindow {
  from: string | null;
  to: string | null;
}

export interface PlanPointWindows {
  loading: PlanPointWindow | null;
  unloading: PlanPointWindow | null;
}

/** Odcinek „wg planu TMS” (API `vehicle-plan.ts`, `TmsPlanSegment`). */
export interface TmsPlanSegment {
  kind: 'tmsPlan';
  start: string;
  end: string;
  /** Plan rozładunku i dodany czas rozładunku — zdanie z API. */
  note: string;
}

/** Rodzaj odcinka paska — wartości z API (`PlanSegmentKind`). */
export type PlanSegmentKind = 'access' | 'loading' | 'stop' | 'drive' | 'break' | 'rest' | 'unloading';

/** Skąd kilometry i czas jazdy dolotu albo przejazdu (`RouteSource` w API). */
export type PlanSegmentSource = 'here' | 'tms' | 'szacunek';

export interface PlanSegment {
  kind: PlanSegmentKind;
  start: string;
  end: string;
  /** Zawsze szacunek. */
  estimate: true;
  source?: PlanSegmentSource;
  /** Na czym oparty odcinek — zdanie z API. */
  note?: string;
}

/**
 * Skąd wiersz planu — `planItemSource` w `vehicle-plan.ts` (omówienie
 * 01.10.2026: „to nie jest stan, tylko … proweniencja”). `orderOrigin` —
 * zlecenie spoza TMS (prezlecenie, dane pokazowe).
 */
export interface PlanSource {
  code: string;
  label: string;
  detail: string;
  orderOrigin: string | null;
}

export interface PlanMonth {
  id: number;
  from: string;
  to: string;
  status: string;
  unavailable: boolean;
}

export interface PlanVehicle {
  id: number;
  registrationNumber: string | null;
  name: string | null;
  carType: string | null;
  ftlCaretaker: string | null;
  available: boolean | null;
  operational: boolean | null;
  unavailableStatus: string | null;
  unavailableTo: string | null;
  company: { id: number; name: string } | null;
  driver: { id: number; name: string | null; surname: string | null } | null;
}

/**
 * Kierowca auta w tygodniu z „Harmonogramu planowania kierowców”.
 * `replicated` — wiersz powielony z wcześniejszego tygodnia (tymczasowe
 * powielenie do czasu regularnego źródła pliku), nie wpis z pliku.
 */
export interface DriverWeekDriver {
  driverId: number | null;
  name: string;
  /** URLOP, L4 … z pliku dla tego tygodnia; kilka — po przecinku. */
  status: string | null;
  replicated: boolean;
  /** Poniedziałek tygodnia źródłowego `RRRR-MM-DD`. */
  replicatedFromWeek: string | null;
}

export interface DriverWeek {
  /** Poniedziałek tygodnia ISO `RRRR-MM-DD`. */
  weekStart: string;
  isoWeek: string;
  drivers: DriverWeekDriver[];
}

export interface VehiclePlan {
  from: string;
  to: string;
  days: number;
  vehicle: PlanVehicle;
  items: PlanItem[];
  months: PlanMonth[];
  /** Brak pola — starsze API (aplikacje wdrażają się osobno); traktujemy jak pustą listę. */
  driverWeeks?: DriverWeek[];
  occupancyNote: string;
  /** Opis odcinków paska; brak — starsze API. */
  segmentsNote?: string;
  /** Opis odcinka „wg planu TMS” (12.10.2026); brak — starsze API. */
  tmsPlanNote?: string;
}

export interface FleetPlanRow extends PlanVehicle {
  items: PlanItem[];
  months: PlanMonth[];
  driverWeeks?: DriverWeek[];
  // ── Harmonogram floty wg makiety FTL, Etap 1 (15.10.2026) — tylko
  // z przełącznikiem `fleetScheduleMockup` (API `fleet-mockup.ts`). ──
  /** Auto wchodzi w grę teraz (ta sama reguła co odmowa w rekomendacjach). */
  usable?: boolean;
  /** Czemu auto nie wchodzi w grę — pełnym zdaniem; `null`, gdy wchodzi. */
  outOfPlanningReason?: string | null;
  /** „Wolny od · gdzie”; `null` — auto wyłączone z planowania. */
  release?: FleetRelease | null;
  /** Wolne teraz, za 6, 12 i 24 h (kolejność jak `counters.free`). */
  freeAt?: boolean[];
  markers?: FleetMarkers;
  gaps?: FleetGap[];
  /** Pasujące zlecenia (`fleetMatchingOrders`); `null` — jeszcze nie policzone. */
  matching?: { count: number | null; refusal: string | null } | null;
}

/** „Wolny od · gdzie” (API `fleet-mockup.ts`, `FleetRelease`). */
export interface FleetRelease {
  /** Kiedy auto się zwalnia; przeszłość = wolne teraz. */
  freeFrom: string | null;
  place: { country: string | null; postalCode: string | null; city: string | null } | null;
  /** `order` — rozładunek obsadzenia, `gps` — świeży odczyt GPS. */
  source: 'order' | 'gps' | null;
  fromOrder: string | null;
  fromOrderId: number | null;
  measuredAt: string | null;
  /** Czemu nie znamy czasu albo miejsca. */
  reason: string | null;
}

/** Żetony przy aucie (makieta pkt 9.1). */
export interface FleetMarkers {
  /** Niepotwierdzone alerty przy obsadzeniach (jak baner na karcie zlecenia). */
  alerts: number;
  /** Auto bez kierowcy w kartotece TMS. */
  noDriver: boolean;
  /** Auto ma żywy wariant. */
  hasVariant: boolean;
}

/** Luka w planie auta (API `fleet-mockup.ts`, `FleetGap`). */
export interface FleetGap {
  from: string;
  /** Koniec rysowany na osi (najpóźniej koniec widoku). */
  to: string;
  /** Początek następnego obsadzenia; `null` — po luce auto nie ma obsadzeń. */
  until: string | null;
  /** Pasujące zlecenia z załadunkiem w luce; `null` — za horyzontem albo nie policzone. */
  matching?: number | null;
}

/** Liczniki nad osią (API `fleetCounters`). */
export interface FleetCounters {
  total: number;
  free: Array<{ hours: number; count: number }>;
  withVariant: number;
  outOfPlanning: number;
}

/** Stan liczenia pasujących zleceń w tle (`fleet-matches.service.ts`). */
export interface FleetMatchingState {
  status: 'ready' | 'refreshing' | 'pending' | 'failed';
  computedAt: string | null;
  horizonDays: number;
  error: string | null;
}

export interface FleetPlan {
  from: string;
  to: string;
  days: number;
  vehicles: FleetPlanRow[];
  caretakers: string[];
  occupancyNote: string;
  /** Opis odcinków paska; brak — starsze API. */
  segmentsNote?: string;
  /** Opis odcinka „wg planu TMS” (12.10.2026); brak — starsze API. */
  tmsPlanNote?: string;
  /** Liczniki nad osią — tylko z `fleetScheduleMockup`. */
  counters?: FleetCounters;
  /** Liczenie pasujących zleceń — tylko z `fleetMatchingOrders`. */
  matching?: FleetMatchingState;
}

/** Zlecenie pasujące do auta — okno luki (API `FleetMatchOrder`). */
export interface FleetMatchOrder {
  userOfferId: number;
  externalId: string | null;
  planningStatus: string;
  from: string;
  to: string;
  fromDate: string;
  /** Dolot w km — szacunek. */
  accessKm: number;
  onTime: boolean | null;
  delivery: boolean | null;
  warnings: number;
  alreadyCandidate: boolean;
}

export interface FleetMatches {
  status: FleetMatchingState['status'];
  computedAt: string | null;
  horizonDays: number | null;
  error: string | null;
  refusal: string | null;
  /** Auto jest w wyniku przeliczenia. */
  known: boolean;
  orders: FleetMatchOrder[];
}

/** Pasujące zlecenia auta w luce (`from` – `until`, ISO); bez luki — cały horyzont. */
export async function fetchFleetMatches(
  carId: number | string,
  gap: { from?: string; until?: string | null } = {},
): Promise<FleetMatches> {
  const { data } = await axiosNest.get<FleetMatches>(`/car-schedules/fleet/matches/${carId}`, {
    params: params({ from: gap.from, until: gap.until ?? undefined }),
  });
  return data;
}

/** Filtry harmonogramu floty — klucze to parametry zapytania API. */
export interface FleetPlanFilters {
  /** Pierwszy dzień widoku `RRRR-MM-DD` (dzień polski). */
  from?: string;
  to?: string;
  q?: string;
  companyId?: number | string;
  carType?: string;
  caretaker?: string;
  busy?: 'assigned' | 'free';
  /** „Pokaż także archiwalne” — bez klucza API pomija auta poza eksploatacją. */
  includeArchived?: boolean;
  /**
   * „Pokaż: z alertem / z wariantem” (15.10.2026, układ wg makiety); z pulpitem
   * planisty (20.10.2026) także wolne teraz, zajęte teraz, bez pozycji.
   */
  show?: Exclude<FleetShow, ''>;
}

/** Puste wartości nie idą w zapytaniu — API traktuje je jak brak filtra. */
function params(filters: object): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === null || value === '') continue;
    out[key] = String(value);
  }
  return out;
}

/**
 * Plan bez „zajęte do” wg planu TMS — przełącznik `occupiedUntilTmsPlan`
 * wyłączony (12.10.2026, wersja „obecna” = produkcja 0.31.3). API przy
 * wyłączonym przełączniku tych pól i tak nie oddaje; ekran zdejmuje je
 * jeszcze raz, żeby tabela, legenda i opis nie zależały od kolejności
 * wdrożeń.
 */
export function withoutTmsPlan<P extends { items: PlanItem[]; tmsPlanNote?: string }>(plan: P): P {
  const { tmsPlanNote: _note, ...rest } = plan;
  return {
    ...rest,
    items: plan.items.map(({ occupiedUntil: _u, occupiedUntilSource: _s, tmsPlanSegment: _t, ...item }) => item),
  } as P;
}

/** Harmonogram floty bez „zajęte do” wg planu TMS — jak `withoutTmsPlan`. */
export function fleetWithoutTmsPlan(plan: FleetPlan): FleetPlan {
  const { tmsPlanNote: _note, ...rest } = plan;
  return {
    ...rest,
    vehicles: plan.vehicles.map((v) => withoutTmsPlan(v)),
  };
}

export async function fetchVehiclePlan(
  carId: number | string,
  window: { from?: string; to?: string } = {},
): Promise<VehiclePlan> {
  const { data } = await axiosNest.get<VehiclePlan>('/car-schedules/plan', {
    params: params({ carId, ...window }),
  });
  return data;
}

export async function fetchFleetPlan(filters: FleetPlanFilters = {}): Promise<FleetPlan> {
  const { data } = await axiosNest.get<FleetPlan>('/car-schedules/fleet', {
    params: params(filters),
  });
  return data;
}

/** Rodzaj odcinka aktywności kierowcy — `ActivityKind` w API. */
export type DriverActivityKind = 'drive' | 'work' | 'break' | 'rest' | 'availability' | 'unknown';

export interface DriverActivitySpan {
  kind: DriverActivityKind;
  /** Status dostawcy dosłownie (`Drive`, `Work`, `Pause`…). */
  status: string;
  start: string;
  end: string;
  /** Czynność trwała w chwili pobrania osi. */
  open: boolean;
  vehiclePlate: string | null;
  /** Jazda albo praca innym pojazdem niż ten z karty. */
  otherVehicle: boolean;
}

export interface DriverActivityWeek {
  weekStart: string;
  weekEnd: string;
  /** Jazda wg tachografu do `coveredUntil` (h). */
  drivenHours: number;
  coveredFrom: string | null;
  coveredUntil: string;
}

/**
 * Aktywność kierowcy pojazdu w oknie planu (07.10.2026) — oś czasu
 * tachografu z eco-trucka i tygodniowy czas jazdy. API:
 * `vehicle-schedules/driver-activity.ts`. `status` inny niż `ok` — brak
 * danych z powodem (`reason`), bez odcinków.
 */
export interface DriverActivity {
  from: string;
  to: string;
  status: 'ok' | 'noDriver' | 'noTelematics' | 'stale';
  reason: string | null;
  /**
   * Oś starsza niż parametr, ale z zapisanymi wpisami przeszłymi
   * (24.10.2026, S-24.10-07, przełącznik `staleTachographPast` w API) —
   * `status: 'stale'`, odcinki są. Brak — jak dotąd: `stale` bez odcinków.
   */
  pastOnly?: true;
  /** Próg świeżości osi (h) — do podpisu „starszy niż … h”; tylko przy `pastOnly`. */
  maxAgeHours?: number;
  until: string | null;
  spans: DriverActivitySpan[];
  weeks: DriverActivityWeek[];
  weeklyLimitHours: number;
  note: string;
}

export async function fetchDriverActivity(
  carId: number | string,
  window: { from?: string; to?: string } = {},
): Promise<DriverActivity> {
  const { data } = await axiosNest.get<DriverActivity>('/car-schedules/driver-activity', {
    params: params({ carId, ...window }),
  });
  return data;
}

// --- Etap 1 makiety FTL: karta i harmonogram pojazdu (15.10.2026) ---

/**
 * Podgląd zlecenia na osi karty pojazdu, jakby było wariantem tego auta —
 * odcinki szacunku tą samą regułą co pasek wariantu (API
 * `VehicleSchedulesService.vehiclePreview`). Za przełącznikiem
 * `vehiclePreviewSegments` (wyłączony: 404).
 */
export interface VehiclePreview {
  item: PlanItem;
  segmentsNote?: string;
}

export async function fetchVehiclePreview(carId: number | string, orderId: number): Promise<VehiclePreview> {
  const { data } = await axiosNest.get<VehiclePreview>('/car-schedules/preview', {
    params: params({ carId, orderId }),
  });
  return data;
}

/** Rodzaj wpisu historii pojazdu — `VehicleHistoryKind` w API (`vehicle-history.ts`). */
export type VehicleHistoryKind = 'assigned' | 'reassignedAway' | 'unassigned' | 'variantAdded' | 'variantReleased';

export interface VehicleHistoryEntry {
  id: number;
  at: string;
  orderId: number;
  orderNumber: string | null;
  kind: VehicleHistoryKind;
  label: string;
  before: string | null;
  after: string | null;
  who: string | null;
  reason: string | null;
  source: string | null;
}

export interface VehicleHistory {
  carId: number;
  entries: VehicleHistoryEntry[];
  limit: number;
}

/** Historia pojazdu z dzienników zleceń (`vehicleAssignmentHistory`; wyłączony: 404). */
export async function fetchVehicleHistory(carId: number | string): Promise<VehicleHistory> {
  const { data } = await axiosNest.get<VehicleHistory>('/car-schedules/history', {
    params: params({ carId }),
  });
  const view = (data ?? {}) as Partial<VehicleHistory>;
  return {
    carId: Number(carId),
    entries: Array.isArray(view.entries) ? view.entries : [],
    limit: typeof view.limit === 'number' ? view.limit : 0,
  };
}
